import React, { Component } from "react";
import AppBar from "@material-ui/core/AppBar";
import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import Hidden from "@material-ui/core/Hidden";
import Modal from "@material-ui/core/Modal";
import Paper from "@material-ui/core/Paper";
import { ReactSVG } from "react-svg";
import Slide from "@material-ui/core/Slide";
import SwipeableViews from "react-swipeable-views";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import Typography from "@material-ui/core/Typography";
import withStyles from "@material-ui/core/styles/withStyles";
import { AuthConsumer } from "../../common";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Helmet } from "react-helmet-async";
import { faLockAlt } from "@fortawesome/pro-regular-svg-icons";
import { Howl } from "howler";
import RegisterForm from "./RegisterForm";
import LoginForm from "./LoginForm";
import LoginBackground from "./LoginBackground";

const styles = theme => ({
  paper: {
    paddingBottom: theme.spacing(4),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      paddingRight: theme.spacing(2),
      paddingLeft: theme.spacing(2)
    }
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.primary.dark,
    height: "72px",
    width: "72px"
  },
  logo: {
    color: theme.palette.primary.main,
    width: "100%", // Safari
    [theme.breakpoints.down("sm")]: {
      display: "none"
    }
  },
  logoGridItem: {
    margin: "auto"
  },
  toolbar: {
    ...theme.mixins.toolbar,
    minHeight: "80px !important",
    [theme.breakpoints.up("md")]: {
      display: "none"
    }
  },
  loginBox: {
    [theme.breakpoints.down("sm")]: {
      boxShadow: "none"
    }
  },
  contentGrid: {
    [theme.breakpoints.up("md")]: {
      flexGrow: 1,
      alignItems: "center"
    }
  }
});

class Login extends Component {
  state = {
    value: 1,
    transitioning: false
  };

  handleChange = (event, value) => {
    if (this.state.value === value) return;
    this.setState({
      value,
      transitioning: true,
      username: undefined,
      status: undefined
    });
  };

  handleTransitionEnd = () => this.setState({ transitioning: false });

  handleEmailExists = email => {
    this.setState({
      value: 1,
      transitioning: true,
      username: email,
      status: "This account is already registered. Please login."
    });
  };

  componentWillUnmount() {
    if (!this.sound) return;
    this.sound.off();
    this.sound.stop();
    this.sound.unload();
    this.sound = null;
  }

  render = () => {
    const { classes, theme, logo } = this.props;

    let logoImg;
    if (!logo) {
      logoImg = null;
    } else if (typeof logo === "string") {
      logoImg = logo.endsWith("svg") ? (
        <ReactSVG
          src={logo}
          beforeInjection={elm => {
            const cssClass = classes.logo;

            if (!!elm.classList) {
              elm.classList.add(cssClass);
            } else if (elm.nodeName === "svg" || elm.nodeName === "SVG") {
              // https://caniuse.com/#search=classList
              // IE11 really does not do <svg> properly :-/
              let appliedClasses = elm.getAttribute("class") || "";
              appliedClasses = !appliedClasses.split(" ").includes(cssClass)
                ? `${elm.className} ${cssClass}`
                : appliedClasses;
              elm.setAttribute("class", appliedClasses);
            }
          }}
        />
      ) : (
        <img src={logo} className={classes.logo} alt="logo" />
      );
    } else {
      const Logo = logo;
      logoImg = <Logo />;
    }

    return (
      <Slide in>
        <Box display="flex" flexGrow={1} flexDirection="column">
          <Helmet>
            <title>Login</title>
          </Helmet>
          <AuthConsumer>
            {({ isVerifying, isLoading, error }) => {
              if (!isVerifying && !this.sound && this.props.sound) {
                this.sound = new Howl({
                  src: this.props.sound,
                  autoplay: true,
                  loop: true,
                  volume: 0.33
                });
              }

              return (
                <>
                  <Modal open={isLoading || isVerifying}>
                    <Box />
                  </Modal>
                  {!isVerifying && (
                    <Hidden smDown implementation="js">
                      <LoginBackground />
                    </Hidden>
                  )}

                  {!isVerifying && (
                    <Box display="flex" flexDirection="column" flexGrow={1}>
                      <Grid container className={classes.contentGrid}>
                        <Grid md={3} item className={classes.logoGridItem}>
                          {logoImg}
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                          <Paper
                            className={classes.loginBox}
                            elevation={8}
                            square
                          >
                            <AppBar
                              position="static"
                              color="default"
                              elevation={0}
                            >
                              <Tabs
                                value={this.state.value}
                                onChange={this.handleChange}
                                indicatorColor="primary"
                                textColor="secondary"
                                variant="fullWidth"
                              >
                                <Tab label="Get Started" />
                                <Tab label="Login" />
                              </Tabs>
                            </AppBar>
                            <SwipeableViews
                              axis={
                                theme.direction === "rtl" ? "x-reverse" : "x"
                              }
                              index={this.state.value}
                              onTransitionEnd={this.handleTransitionEnd}
                            >
                              <Box p={3} mb={5}>
                                {(this.state.transitioning ||
                                  this.state.value === 0) && (
                                  <>
                                    <Box>
                                      <Typography variant="h5">
                                        Let's Get Started!
                                      </Typography>
                                    </Box>
                                    <Divider />
                                    <RegisterForm
                                      onEmailExists={this.handleEmailExists}
                                    />
                                  </>
                                )}
                              </Box>
                              <Box p={3} mb={5}>
                                {(this.state.transitioning ||
                                  this.state.value === 1) && (
                                  <>
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      paddingBottom="4px"
                                      pl={1}
                                    >
                                      <FontAwesomeIcon
                                        icon={faLockAlt}
                                        size="2x"
                                      />
                                      <Typography
                                        variant="h5"
                                        style={{ marginLeft: "12px" }}
                                      >
                                        Sign in
                                      </Typography>
                                    </Box>
                                    <Divider />
                                    <Typography
                                      variant="subtitle1"
                                      color="error"
                                    >
                                      {error
                                        ? typeof error === "string"
                                          ? error
                                          : "An unexpected error occured.  Please try later."
                                        : null}
                                    </Typography>

                                    <LoginForm
                                      initValues={{
                                        username: this.state.username,
                                        password: "",
                                        remember: false
                                      }}
                                      initStatus={this.state.status}
                                    />
                                  </>
                                )}
                              </Box>
                            </SwipeableViews>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </>
              );
            }}
          </AuthConsumer>
        </Box>
      </Slide>
    );
  };
}

export default withStyles(styles, { name: "Login", withTheme: true })(Login);
